import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Checkbox,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { StepButtonBar } from './StepButtonBar';
import { useAptorApi } from '../../../../../Api';
import { useFilter } from '../../../../../Components/ListFilter';
import { OrderDirection, IGridQueryRequest } from '../../../../../Api/AptorApi';
import MTable from '../../../../../Components/MTable/MTable';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { RepealableName } from '../../../../../Components';
import { EditRequirementFilter } from './EditRequirementFilter';
import moment from 'moment';

interface EditStep1SelectRequirementProps {
  onNext: (data?: any) => void;
  onBack: () => void;
  onCancel: () => void;
  formatMessage: (descriptor: any) => string;
  showBack?: boolean;
}

const EditStep1SelectRequirement: React.FC<EditStep1SelectRequirementProps> = ({
  onNext,
  onBack,
  onCancel,
  formatMessage,
  showBack = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { api } = useAptorApi();
  const [loading, setLoading] = useState(false);
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const isFirstLoadRef = React.useRef(true);

  const selectedRowIdRef = React.useRef<number | null>(null);
  const [selectionVersion, setSelectionVersion] = useState(0);

  const setSelectedRowId = useCallback((id: number | null) => {
    selectedRowIdRef.current = id;
    setSelectionVersion(v => v + 1);
  }, []);

  const toggleRequirement = useCallback((rowId: number) => {
    const next = selectedRowIdRef.current === rowId ? null : rowId;
    setSelectedRowId(next);
  }, [setSelectedRowId]);

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  const handleDataFetch = useCallback(
    async (query: any) => {
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return { data: [], page: 0, pageSize: query.pageSize, totalCount: 0 };
      }

      setLoading(true);
      try {
        const result = await api.searchLawPortalRequirements({
          orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
          orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
          page: query.page,
          pageSize: query.pageSize,
          search: query.search,
          filters: filters,
        } as IGridQueryRequest);

        return result;
      } finally {
        setLoading(false);
      }
    },
    [api, filters],
  );

  const baseColumns = useMemo(() => [
    {
      title: '',
      field: 'select',
      width: '48px',
      sorting: false,
      removable: false,
      hiddenByColumnsButton: true,
      render: (row: any) => (
        <Checkbox
          checked={selectedRowIdRef.current === row.id}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleRequirement(row.id)}
        />
      ),
    },
      {
        title: formatMessage({ id: 'law-portal.requirements.table.name' }),
        field: 'name',
        render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
        hidden: false,
      },
      {
        title: formatMessage({ id: 'law-portal.requirements.table.description' }),
        field: 'description',
        render: (row: any) => <HtmlView variant="body2" value={row.description} />,
        sorting: false,
        hidden: false,
      },
      {
        title: formatMessage({ id: 'law-portal.requirements.table.area' }),
        field: 'area',
        hidden: false,
      },
      {
        title: formatMessage({ id: 'law-portal.requirements.table.changedDate' }),
        field: 'changedDate',
        render: (row: any) => renderDate(row.changedDate),
        hidden: false,
      },
      {
        title: formatMessage({ id: 'law-portal.requirements.table.notes' }),
        field: 'notes',
        render: (row: any) => <OwnNotes {...row} />,
        sorting: false,
        hidden: false,
      },
      {
        title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
        field: 'responsible',
        render: (row: any) => {
          if (row.responsible) {
            const responsiblearr = row?.responsible?.split(',');
            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {responsiblearr?.map((val: any, i: number) => (
                  <span key={i}>{val}</span>
                ))}
              </div>
            );
          }
          return '-';
        },
        sorting: false,
        hidden: false,
      },
  ], [selectionVersion, formatMessage]);


  const handleNext = async () => {
    if (selectedRowIdRef.current) {
      setLoading(true);
      try {
        const info = await (api as any).getRequirement(selectedRowIdRef.current);

        onNext({
          name: info.name,
          shortDescription: info.description,
          area: info.area?.id || null,  
          areaLabel: info.area?.name || null,
          effectiveFrom: info.effectiveFrom,
          effectiveTo: info.effectiveTo,
          responsible: info.responsible,
          selectedRequirement: info,
          selectedRequirementId: selectedRowIdRef.current,
        });
      } catch (err) {
        console.error('Error fetching requirement:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: '100%',
            flexShrink: 0,
            padding: isMobile ? theme.spacing(2) : theme.spacing(3),
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
          }}
        >
          <Box style={{ marginBottom: '16px' }}>
            <EditRequirementFilter
              filters={filters}
              setFilter={setFilter}
            />
          </Box>

          <Box style={{ maxHeight: '500px', overflow: 'auto' }}>
            <MTable
              key={`edit-requirement-table-${JSON.stringify(filters)}`}
              tableRef={tableRef}
              title=""
              columns={baseColumns}
              data={(query) => handleDataFetch(query)}
              isLoading={loading}
              options={{
                pageSize: 10,
                pageSizeOptions: [5, 10, 20, 50],
                search: true,
                sorting: true,
                filtering: false,
                selection: false,
                paging: true,
                showTextRowsSelected: false,
              }}
            />
          </Box>
        </div>
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={!selectedRowIdRef.current}
          formatMessage={formatMessage}
          showBack={showBack}
        />
      </Box>
    </div>
  );
};

export default EditStep1SelectRequirement;
